"use client";

import Image from "next/image";
import Link from "next/link";
import React, { useMemo } from "react";
import Slider from "react-slick";
import styled from "styled-components";

import {
  ArrowIcon,
  Text,
  TextColor,
  TextSize,
  TextVariant,
  TextWeight,
} from "../../components";
// Types
import type { HomepageCategory } from "../../types";

// Skeleton Component
export const HomepageCategoriesSkeleton: React.FC = () => {
  return (
    <div className="category-slider-container">
      <div className="w-32 h-8 bg-gray-200 animate-pulse rounded mx-auto mb-6" />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 px-4">
        {[...Array(4)].map((_, index) => (
          <div key={index} className="relative">
            <div className="w-full h-[670px] bg-gray-200 animate-pulse" />
            <div className="absolute bottom-1/2 left-1/2 transform -translate-x-1/2 translate-y-6">
              <div className="w-24 h-8 bg-gray-300 animate-pulse rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

type HomepageCategoriesProps = {
  homepageCategories: HomepageCategory[];
  isLoading?: boolean;
};

export const HomepageCategories: React.FC<HomepageCategoriesProps> = ({
  homepageCategories,
  isLoading = false,
}) => {
  const baseUrl = useMemo(
    () => process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost/prestashop",
    [],
  );

  // Custom arrow component with SVG
  const CustomArrow = ({
    onClick,
    direction,
  }: {
    onClick?: () => void;
    direction: "prev" | "next";
  }) => (
    <button
      className={`slick-${direction}`}
      onClick={onClick}
      aria-label={`${direction === "prev" ? "Previous" : "Next"} slide`}
    >
      <ArrowIcon />
    </button>
  );

  // Memoize slider settings
  const sliderSettings = useMemo(
    () => ({
      dots: false,
      infinite: true,
      speed: 500,
      slidesToShow: 3,
      slidesToScroll: 1,
      arrows: true,
      autoplay: false,
      prevArrow: <CustomArrow direction="prev" />,
      nextArrow: <CustomArrow direction="next" />,
      responsive: [
        {
          breakpoint: 768,
          settings: {
            slidesToShow: 3,
            slidesToScroll: 1,
          },
        },
        {
          breakpoint: 480,
          settings: {
            slidesToShow: 2,
            slidesToScroll: 1,
          },
        },
        {
          breakpoint: 400,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
          },
        },
      ],
    }),
    [homepageCategories.length],
  );

  // Memoize utility functions
  const getImagePath = useMemo(
    () =>
      (category: HomepageCategory): string => {
        return `${baseUrl}${category.imagePath}`;
      },
    [baseUrl],
  );

  const isInternalLink = useMemo(
    () =>
      (url: string): boolean => {
        if (!url) return false;
        return (
          url.startsWith("/") || url.startsWith(baseUrl) || !url.includes("://")
        );
      },
    [baseUrl],
  );

  const normalizeUrl = useMemo(
    () =>
      (url: string): string => {
        if (!url) return "#";
        if (url.startsWith("/")) return url;
        if (url.startsWith(baseUrl)) return url.replace(baseUrl, "/");
        return url;
      },
    [baseUrl],
  );

  // Memoize processed categories data
  const processedCategories = useMemo(() => {
    return homepageCategories && homepageCategories.length > 0
      ? homepageCategories.map((category) => ({
          ...category,
          imagePath: getImagePath(category),
          normalizedLink: isInternalLink(category.link)
            ? normalizeUrl(category.link)
            : category.link,
          isInternal: isInternalLink(category.link),
        }))
      : [];
  }, [homepageCategories, getImagePath, isInternalLink, normalizeUrl]);

  if (isLoading) return <HomepageCategoriesSkeleton />;

  if (homepageCategories.length === 0) return null;

  const renderCategoryItem = (category: (typeof processedCategories)[0]) => {
    const content = (
      <div className="category-slider-item group cursor-pointer">
        <Image
          src={category.imagePath}
          alt={category.title}
          width={300}
          height={670}
          className="w-full h-auto object-cover max-h-[670px]"
          sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
        />
        <div className="label">
          <p>{category.title}</p>
        </div>
      </div>
    );

    if (category.isInternal) {
      return (
        <Link key={category.id} href={category.normalizedLink}>
          {content}
        </Link>
      );
    } else {
      return (
        <a
          key={category.id}
          href={category.link}
          target="_blank"
          rel="noopener noreferrer"
        >
          {content}
        </a>
      );
    }
  };

  return (
    <div className="w-full max-w-screen-2xl mx-auto">
      <div className="flex justify-center my-6">
        <Text
          text="Kategorie"
          weight={TextWeight.SEMIBOLD}
          textTransform="uppercase"
          className="tracking-widest"
          color={TextColor.BLACK}
          fontSize={TextSize.XS}
          variant={TextVariant.H4}
        />
      </div>

      <HomepageCategoriesWrapper>
        <div className="category-slider-container">
          <div className="category-slider">
            <Slider {...sliderSettings}>
              {processedCategories.map((category) => (
                <div key={category.id}>{renderCategoryItem(category)}</div>
              ))}
            </Slider>
          </div>
        </div>
      </HomepageCategoriesWrapper>
    </div>
  );
};

const HomepageCategoriesWrapper = styled.div`
  width: 100%;
  max-width: calc(100vw - 17px);

  ${({ theme }) => theme.mediaQueries.sm} {
    max-width: 100vw;
  }

  .category-slider-container {
    width: 100%;
    text-align: center;
    position: relative;

    @media (max-width: 480px) {
      padding: 0;
    }

    .category-slider {
      height: auto;

      .category-slider-item {
        position: relative;
        transition: opacity 0.3s;
        max-height: 670px;
        margin: 0 2.5px;

        ${({ theme }) => theme.mediaQueries.sm} {
          margin: 0;
        }

        &:hover {
          opacity: 0.75;
        }

        .label {
          width: 100%;
          height: 50px;
          margin: 0 auto;
          display: flex;
          justify-content: center;
          align-items: center;
          text-align: center;
          position: absolute;
          bottom: calc(50% - 27px);

          p {
            font-size: 45px;
            font-style: normal;
            font-weight: 700;
            line-height: 57px;
            letter-spacing: 0.1em;
            text-align: center;
            text-transform: uppercase;
            color: white;
            margin: 0;

            @media (max-width: 480px) {
              font-size: 30px;
              font-weight: 700;
              line-height: 38px;
              letter-spacing: 0.2em;
            }
          }
        }
      }
    }
  }

  .slick-list {
    overflow: hidden;
    width: 100% !important;
  }

  .slick-track {
    display: flex;
    align-items: stretch;
  }

  .slick-slide {
    > div {
      height: 100%;
    }
  }

  .slick-next,
  .slick-prev {
    position: absolute;
    z-index: 1;
    width: 30px;
    height: 60px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    background: none;
    border: none;
    outline: none;
    display: block;
    fill: #fff;

    svg {
      width: 30px;
      height: 60px;
      fill: #fff;
    }

    &:before {
      display: none;
    }

    &:hover {
      opacity: 0.8;
    }
  }

  .slick-prev {
    left: 0;
  }

  .slick-next {
    right: 0;

    svg {
      transform: rotate(180deg);
    }
  }

  // Hide arrows on mobile
  @media (max-width: 768px) {
    .slick-next,
    .slick-prev {
      display: none !important;
    }
  }
`;

export default HomepageCategories;
